import { useEffect, useState, useMemo } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
    Plus,
    Trash2,
    ExternalLink,
    LogOut,
    Package,
    Truck,
    CheckCircle2,
    Clock,
    Search,
    X,
    Copy,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { CarrierBadge } from "../components/app/CarrierBadge";

const CARRIER_OPTIONS = [
    { value: "NACEX", label: "NACEX" },
    { value: "CORREOS_EXP", label: "CORREOS EXP." },
    { value: "CTT", label: "CTT" },
    { value: "DPD", label: "DPD" },
    { value: "OUTRA", label: "OUTRA" },
];

function computeStats(items) {
    const s = { total: items.length, in_transit: 0, delivered: 0, pending: 0 };
    for (const it of items) {
        if (it.status_code === "ENTREGUE") s.delivered++;
        else if (it.status_code === "RECOLHIDO") s.pending++;
        else s.in_transit++;
    }
    return s;
}

function fmt(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("pt-PT", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

export default function AdminDashboard() {
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState({ total: 0, in_transit: 0, delivered: 0, pending: 0 });
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(true);
    const [search, setSearch] = useState("");
    const [openForm, setOpenForm] = useState(false);

    async function refresh() {
        setBusy(true);
        try {
            const t = await api.get("/admin/trackings");
            setItems(t.data);
            setStats(computeStats(t.data));
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    useEffect(() => {
        if (user && user !== false) refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const filtered = useMemo(() => {
        if (!search.trim()) return items;
        const q = search.toLowerCase();
        return items.filter(
            (it) =>
                it.code.toLowerCase().includes(q) ||
                it.tracking_number.toLowerCase().includes(q) ||
                (it.order_number || "").toLowerCase().includes(q) ||
                (it.reference || "").toLowerCase().includes(q) ||
                it.carrier_label.toLowerCase().includes(q),
        );
    }, [items, search]);

    if (loading) return null;
    if (!user || user === false) return <Navigate to="/admin/login" replace />;

    async function handleLogout() {
        await logout();
        navigate("/admin/login", { replace: true });
    }

    async function handleDelete(id) {
        if (!window.confirm("Tem a certeza que pretende eliminar este envio?")) return;
        try {
            await api.delete(`/admin/trackings/${id}`);
            toast.success("Envio eliminado.");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    function copyLink(it) {
        const ref = it.order_number || it.code;
        const url = `${window.location.origin}/track/${encodeURIComponent(ref)}`;
        navigator.clipboard.writeText(url);
        toast.success("Ligação copiada para a área de transferência.");
    }

    return (
        <div className="min-h-screen bg-slate-50">
            <Toaster position="top-right" richColors />

            {/* Top bar */}
            <header className="bg-white border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Link to="/" className="flex items-center gap-2">
                            <div className="relative">
                                <div className="w-6 h-6 bg-[var(--tp-blue)]" />
                                <div className="absolute -right-1 -bottom-1 w-2.5 h-2.5 bg-[var(--tp-orange)]" />
                            </div>
                            <span className="font-black tracking-tight">
                                TRACKING<span className="text-[var(--tp-orange)]">PLUS</span>
                            </span>
                        </Link>
                        <span className="hidden sm:inline-block ml-3 text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500 border border-slate-300 px-2 py-0.5">
                            Backoffice
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="hidden md:inline text-sm text-slate-600">
                            {user.email}
                        </span>
                        <button
                            data-testid="logout-button"
                            onClick={handleLogout}
                            className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 text-xs font-bold uppercase tracking-wider hover:bg-slate-50 transition"
                        >
                            <LogOut className="w-4 h-4" /> Sair
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
                <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                    <div>
                        <span className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">
                            Dashboard
                        </span>
                        <h1 className="mt-1 text-3xl lg:text-4xl font-black tracking-tight">
                            Gestão de envios
                        </h1>
                    </div>
                    <button
                        data-testid="open-add-tracking-button"
                        onClick={() => setOpenForm(true)}
                        className="inline-flex items-center gap-2 h-11 px-5 bg-[var(--tp-orange)] hover:bg-[var(--tp-orange-hover)] text-white font-bold uppercase tracking-wider transition-all duration-300 hover:-translate-y-0.5"
                    >
                        <Plus className="w-4 h-4" /> Novo envio
                    </button>
                </div>

                {/* KPIs */}
                <section className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 mb-8" data-testid="kpi-cards">
                    <Kpi label="Total de envios" value={stats.total} Icon={Package} />
                    <Kpi label="Em trânsito" value={stats.in_transit} Icon={Truck} accent />
                    <Kpi label="Entregues" value={stats.delivered} Icon={CheckCircle2} />
                    <Kpi label="Pendentes" value={stats.pending} Icon={Clock} />
                </section>

                {/* Search */}
                <div className="mb-4 flex items-center gap-3">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            data-testid="admin-search-input"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Pesquisar por código, nº de tracking, referência..."
                            className="w-full h-10 pl-9 pr-3 border border-slate-300 focus:border-[var(--tp-blue)] focus:ring-1 focus:ring-[var(--tp-blue)] outline-none bg-white text-sm"
                        />
                    </div>
                </div>

                {/* Table */}
                <section className="bg-white border border-slate-200 overflow-x-auto">
                    <table className="w-full text-sm" data-testid="trackings-table">
                        <thead>
                            <tr className="border-b border-slate-200 text-left">
                                <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-widest text-slate-500">Código</th>
                                <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-widest text-slate-500">Transportadora</th>
                                <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-widest text-slate-500">Nº Encomenda</th>
                                <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-widest text-slate-500">Nº Tracking</th>
                                <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-widest text-slate-500">Referência</th>
                                <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-widest text-slate-500">Estado</th>
                                <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-widest text-slate-500">Criado em</th>
                                <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-widest text-slate-500 text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {busy && (
                                <tr><td colSpan={8} className="px-5 py-12 text-center text-slate-500">A carregar...</td></tr>
                            )}
                            {!busy && filtered.length === 0 && (
                                <tr><td colSpan={8} className="px-5 py-16 text-center">
                                    <Package className="w-8 h-8 mx-auto text-slate-300 mb-3" />
                                    <div className="font-bold text-slate-700">Sem envios</div>
                                    <div className="text-slate-500 mt-1">Clique em "Novo envio" para começar.</div>
                                </td></tr>
                            )}
                            {!busy && filtered.map((it) => (
                                <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50 transition" data-testid={`tracking-row-${it.code}`}>
                                    <td className="px-5 py-4 font-mono font-bold text-slate-900">{it.code}</td>
                                    <td className="px-5 py-4"><CarrierBadge code={it.carrier} /></td>
                                    <td className="px-5 py-4 font-mono text-slate-700">{it.order_number || <span className="text-slate-400">—</span>}</td>
                                    <td className="px-5 py-4 font-mono text-slate-600">{it.tracking_number}</td>
                                    <td className="px-5 py-4 text-slate-700">{it.reference || <span className="text-slate-400">—</span>}</td>
                                    <td className="px-5 py-4">
                                        <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-700">
                                            <span className={`w-1.5 h-1.5 rounded-full ${
                                                it.status_code === "ENTREGUE" ? "bg-green-500" :
                                                it.status_code === "RECOLHIDO" ? "bg-slate-400" :
                                                ["ENTREGA_FALHADA", "EXCECAO", "DEVOLVIDO"].includes(it.status_code) ? "bg-red-500" :
                                                "bg-[var(--tp-orange)]"
                                            }`} />
                                            {it.status_label}
                                        </span>
                                    </td>
                                    <td className="px-5 py-4 text-slate-500 font-mono text-xs">{fmt(it.created_at)}</td>
                                    <td className="px-5 py-4">
                                        <div className="flex items-center justify-end gap-1">
                                            {it.carrier_url && (
                                                <a href={it.carrier_url} target="_blank" rel="noopener noreferrer"
                                                    title={`Verificar em ${it.carrier_label}`}
                                                    data-testid={`carrier-link-${it.code}`}
                                                    className="p-2 hover:bg-blue-50 transition text-[var(--tp-blue)]">
                                                    <Truck className="w-4 h-4" />
                                                </a>
                                            )}
                                            <button onClick={() => copyLink(it)} title="Copiar link" data-testid={`copy-link-${it.code}`}
                                                className="p-2 hover:bg-slate-100 transition text-slate-600">
                                                <Copy className="w-4 h-4" />
                                            </button>
                                            <Link to={`/track/${it.order_number || it.code}`} target="_blank" rel="noopener noreferrer" title="Abrir tracking"
                                                data-testid={`view-tracking-${it.code}`}
                                                className="p-2 hover:bg-slate-100 transition text-slate-600">
                                                <ExternalLink className="w-4 h-4" />
                                            </Link>
                                            <button onClick={() => handleDelete(it.id)} title="Eliminar"
                                                data-testid={`delete-tracking-${it.code}`}
                                                className="p-2 hover:bg-red-50 transition text-red-600">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            </main>

            {openForm && (
                <AddTrackingModal
                    onClose={() => setOpenForm(false)}
                    onCreated={() => {
                        setOpenForm(false);
                        refresh();
                    }}
                />
            )}
        </div>
    );
}

function Kpi({ label, value, Icon, accent }) {
    return (
        <div className="bg-white p-5 lg:p-6">
            <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{label}</span>
                <Icon className={`w-4 h-4 ${accent ? "text-[var(--tp-orange)]" : "text-slate-400"}`} />
            </div>
            <div className={`mt-2 text-3xl lg:text-4xl font-black tracking-tight ${accent ? "text-[var(--tp-orange)]" : "text-slate-900"}`}>
                {value}
            </div>
        </div>
    );
}

function AddTrackingModal({ onClose, onCreated }) {
    const [carrier, setCarrier] = useState("NACEX");
    const [orderNumber, setOrderNumber] = useState("");
    const [trackingNumber, setTrackingNumber] = useState("");
    const [trackingUrl, setTrackingUrl] = useState("");
    const [reference, setReference] = useState("");
    const [recipientName, setRecipientName] = useState("");
    const [recipientLocality, setRecipientLocality] = useState("");
    const [recipientPostalCode, setRecipientPostalCode] = useState("");
    const [err, setErr] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    async function submit(e) {
        e.preventDefault();
        setErr(null);
        setSubmitting(true);
        try {
            const res = await api.post("/admin/trackings", {
                carrier,
                order_number: orderNumber.trim(),
                tracking_number: trackingNumber.trim(),
                reference: reference.trim() || null,
                tracking_url: carrier === "OUTRA" ? trackingUrl.trim() || null : null,
                recipient_name: recipientName.trim() || null,
                recipient_locality: recipientLocality.trim() || null,
                recipient_postal_code: recipientPostalCode.trim() || null,
            });
            toast.success(`Envio criado: ${res.data.code}`);
            onCreated();
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div
            className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="add-tracking-modal"
            onClick={onClose}
        >
            <div
                className="bg-white border border-slate-200 w-full max-w-lg p-6 lg:p-8 relative"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    data-testid="close-add-tracking"
                    className="absolute top-4 right-4 p-2 hover:bg-slate-100 text-slate-500"
                >
                    <X className="w-4 h-4" />
                </button>

                <span className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">
                    Backoffice
                </span>
                <h2 className="mt-1 text-2xl font-black tracking-tight">Novo envio</h2>
                <p className="mt-1 text-sm text-slate-600">
                    Selecione a transportadora e introduza o número de tracking.
                </p>

                <form onSubmit={submit} className="mt-6 space-y-4">
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-slate-600 mb-1.5">
                            Transportadora
                        </label>
                        <select
                            data-testid="carrier-select"
                            value={carrier}
                            onChange={(e) => setCarrier(e.target.value)}
                            className="w-full h-11 px-3 border border-slate-300 focus:border-[var(--tp-blue)] focus:ring-1 focus:ring-[var(--tp-blue)] outline-none bg-white text-sm"
                        >
                            {CARRIER_OPTIONS.map((c) => (
                                <option key={c.value} value={c.value} data-testid={`carrier-option-${c.value}`}>
                                    {c.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-slate-600 mb-1.5">
                            Nº Encomenda
                        </label>
                        <input
                            data-testid="order-number-input"
                            value={orderNumber}
                            onChange={(e) => setOrderNumber(e.target.value)}
                            required
                            placeholder="Ex.: ENC-2026-001"
                            className="w-full h-11 px-3 font-mono border border-slate-300 focus:border-[var(--tp-blue)] focus:ring-1 focus:ring-[var(--tp-blue)] outline-none bg-white"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-slate-600 mb-1.5">
                            Número de Tracking
                        </label>
                        <input
                            data-testid="tracking-number-input"
                            value={trackingNumber}
                            onChange={(e) => setTrackingNumber(e.target.value)}
                            required
                            placeholder="Ex.: 1234567890"
                            className="w-full h-11 px-3 font-mono border border-slate-300 focus:border-[var(--tp-blue)] focus:ring-1 focus:ring-[var(--tp-blue)] outline-none bg-white"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-slate-600 mb-1.5">
                            Referência / Cliente (opcional)
                        </label>
                        <input
                            data-testid="reference-input"
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            placeholder="Ex.: João Silva — Encomenda #45"
                            className="w-full h-11 px-3 border border-slate-300 focus:border-[var(--tp-blue)] focus:ring-1 focus:ring-[var(--tp-blue)] outline-none bg-white"
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-widest text-slate-600 mb-1.5">
                                Nome do destinatário
                            </label>
                            <input
                                data-testid="recipient-name-input"
                                value={recipientName}
                                onChange={(e) => setRecipientName(e.target.value)}
                                placeholder="Ex.: Maria Costa"
                                className="w-full h-11 px-3 border border-slate-300 focus:border-[var(--tp-blue)] focus:ring-1 focus:ring-[var(--tp-blue)] outline-none bg-white"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-widest text-slate-600 mb-1.5">
                                Localidade
                            </label>
                            <input
                                data-testid="recipient-locality-input"
                                value={recipientLocality}
                                onChange={(e) => setRecipientLocality(e.target.value)}
                                placeholder="Ex.: Porto"
                                className="w-full h-11 px-3 border border-slate-300 focus:border-[var(--tp-blue)] focus:ring-1 focus:ring-[var(--tp-blue)] outline-none bg-white"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-slate-600 mb-1.5">
                            Código Postal {carrier === "OUTRA" && <span className="text-[var(--tp-orange)]">(necessário p/ Cargoboard)</span>}
                        </label>
                        <input
                            data-testid="recipient-postal-code-input"
                            value={recipientPostalCode}
                            onChange={(e) => setRecipientPostalCode(e.target.value)}
                            placeholder="Ex.: 4700-000"
                            className="w-full h-11 px-3 border border-slate-300 focus:border-[var(--tp-blue)] focus:ring-1 focus:ring-[var(--tp-blue)] outline-none bg-white"
                        />
                    </div>

                    {carrier === "OUTRA" && (
                        <div className="border-l-2 border-[var(--tp-orange)] pl-4 -ml-1 py-1">
                            <label className="block text-xs font-bold uppercase tracking-widest text-slate-600 mb-1.5">
                                URL de tracking da transportadora
                            </label>
                            <input
                                data-testid="tracking-url-input"
                                type="url"
                                value={trackingUrl}
                                onChange={(e) => setTrackingUrl(e.target.value)}
                                required
                                placeholder="https://transportadora.com/tracking?id=12345"
                                className="w-full h-11 px-3 font-mono text-sm border border-slate-300 focus:border-[var(--tp-orange)] focus:ring-1 focus:ring-[var(--tp-orange)] outline-none bg-white"
                            />
                            <p className="mt-1.5 text-xs text-slate-500">
                                O sistema irá tentar ler automaticamente os eventos deste link, mascarando sempre a origem como LOGISTICA PLUS.
                            </p>
                        </div>
                    )}

                    {err && (
                        <div className="border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="add-tracking-error">
                            {err}
                        </div>
                    )}

                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="h-11 px-4 border border-slate-300 text-sm font-bold uppercase tracking-wider hover:bg-slate-50"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="submit-tracking-button"
                            className="h-11 px-5 bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold uppercase tracking-wider disabled:opacity-60"
                        >
                            {submitting ? "A criar..." : "Criar envio"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
